import { useEffect } from "react";

/**
 * Custom hook that triggers a callback when a click is detected outside of the specified element.
 *
 * @param {React.RefObject} ref - The reference to the element to detect outside clicks for.
 * @param {Function} callback - The callback function to be executed when an outside click is detected.
 * @param {{ ignoreSelector?: string }} [options] - If `ignoreSelector` is set, mousedown targets inside a matching ancestor are ignored (treated as not "outside").
 * @returns {void}
 */
const useOutsideClick = (ref, callback, options) => {
  const ignoreSelector = options?.ignoreSelector;

  useEffect(() => {
    /**
     * Fires `callback` when mousedown is outside `ref` (and not inside `ignoreSelector`).
     *
     * @param {MouseEvent} event Document mousedown event.
     * @returns {void}
     */
    const handleClickOutside = (event) => {
      const target = event.target;
      const targetEl =
        target instanceof Element ? target : target?.parentElement ?? null;
      if (ignoreSelector && targetEl?.closest(ignoreSelector)) {
        return;
      }
      if (ref.current && !ref.current.contains(target)) {
        callback();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, ignoreSelector]);
};

export default useOutsideClick;
